import { Formik, Form, Field, FormikProps } from "formik";
import React, { useMemo, useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";

import { IEditableSectionProps } from "@/components/EditableSection";
import { createOrUpdateCashPaymentDetails, fetchCashPaymentDetails } from "@/data/companies";
import { useCompanyContext } from "@/hooks/User";
import { useToasts } from "@/views/components/Toast/useToasts";

interface ISupplierBankingSectionProps {
  editableProps: IEditableSectionProps;
}

export interface ICashPaymentDetails {
  companyId?: number;
  cashMethodPL: boolean;
}

const defaultValues: ICashPaymentDetails = {
  companyId: undefined,
  cashMethodPL: false,
};

export const CashPaymentSection = ({ editableProps }: ISupplierBankingSectionProps) => {
  const Company = useCompanyContext();
  const { t } = useTranslation("session");
  const { addToast } = useToasts();

  const submitFormRef = useRef<(() => void) | null>(null);

  const { data: fetchedCashPaymentDetails, refetch } = useQuery(
    `/api/companies/cash-payment-details/${Company.id}`,
    () => fetchCashPaymentDetails(Company.id),
    {
      enabled: !!Company.id,
    },
  );

  const [stablePaymentDetail, setStablePaymentDetail] = useState<ICashPaymentDetails>({
    ...defaultValues,
    companyId: Company.id,
  });

  useEffect(() => {
    if (!fetchedCashPaymentDetails) return;

    const fetchedValue = fetchedCashPaymentDetails.cashMethodPL;

    if (typeof fetchedValue === "boolean") {
      setStablePaymentDetail({
        ...fetchedCashPaymentDetails,
        cashMethodPL: fetchedValue,
        companyId: Company.id,
      });
    }
  }, [fetchedCashPaymentDetails, Company.id]);

  const paymentDetail = useMemo(() => {
    return stablePaymentDetail;
  }, [stablePaymentDetail]);

  const handleSubmit = async (values: ICashPaymentDetails) => {
    try {
      await createOrUpdateCashPaymentDetails({
        companyId: Company.id,
        cashMethodPL: values.cashMethodPL,
      });

      editableProps.handleCancelClick();
      await refetch();

      addToast({
        text: t("USERS.SETTINGS.BANKING_DETAILS.FORM.TOAST_PAYMENT_DETAILS.SUCCESS", { action: "save" }),
        type: "success",
      });
    } catch (e) {
      addToast({
        text: t("USERS.SETTINGS.BANKING_DETAILS.FORM.TOAST_PAYMENT_DETAILS.FAILURE", { action: "save" }),
        type: "danger",
      });
    }
  };

  useEffect(() => {
    if (editableProps.saveClicked && submitFormRef.current) {
      submitFormRef.current();
      editableProps.setSaveClicked(false);
    }
  }, [editableProps.saveClicked]);

  if (!Company.id || !paymentDetail) {
    return <p>{t("USERS.SETTINGS.BANKING_DETAILS.FORM.PAYMENT_DETAILS_LOADING")}</p>;
  }

  if (!editableProps.editMode) {
    return <p>{t("USERS.SETTINGS.BANKING_DETAILS.FORM.PAYMENT_DETAILS_CASH_METHOD")}</p>;
  }

  return (
    <Formik initialValues={paymentDetail} onSubmit={handleSubmit} enableReinitialize>
      {(formik: FormikProps<ICashPaymentDetails>) => {
        submitFormRef.current = formik.submitForm;

        return (
          <Form>
            <label>
              <Field type="checkbox" name="cashMethodPL" />{" "}
              {t("USERS.SETTINGS.BANKING_DETAILS.FORM.PAYMENT_DETAILS_CASH_METHOD")}
            </label>
          </Form>
        );
      }}
    </Formik>
  );
};
